import type { LinearRGB } from '../../core/color';
import type { VertexStyle } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { hullPoint, type FormedTemplate } from './shipForm';

/** Where a ship is drawn from (docs/plans/ships.md 4): beyond 250 m, within 250 m, within 60 m. */
export type ShipTier = 'far' | 'mid' | 'near';

export interface HullPalette {
  readonly hull: LinearRGB;
  /** Wales, a shade lighter than the hull: the painted pale lines along the hull (R9, R17). */
  readonly wale: LinearRGB;
  /** Rails, posts and port frames: the painting's pale strokes on castles and waist (R3, R34). */
  readonly rail: LinearRGB;
  /** The inside of an open port. */
  readonly port: LinearRGB;
  readonly rope: LinearRGB;
}

export interface HullStyles {
  readonly hull: VertexStyle;
  readonly wale: VertexStyle;
  readonly rail: VertexStyle;
  readonly port: VertexStyle;
  readonly rope: VertexStyle;
}

const STATIONS: Readonly<Record<ShipTier, number>> = { far: 12, mid: 24, near: 24 };
const CASTLE_INSET = 0.97;
/** The waist's cap rail: how far it reaches below and above the rail and out past the bulwark. */
export const CAP_RAIL = { below: 0.04, above: 0.07, overhang: 0.03 } as const;
/** A castle's rail runs this far inside the castle wall's outer face. */
export const RAIL_INSET = 0.07;

export function stationsOf(tier: ShipTier): readonly number[] {
  const n = STATIONS[tier];
  return Array.from({ length: n + 1 }, (_, i) => i / n);
}

/** The samples of a stretch of the hull: its ends and every station between them. */
export function samplesOver(tier: ShipTier, from: number, to: number): readonly number[] {
  const inside = stationsOf(tier).filter((s) => s > from + 1e-9 && s < to - 1e-9);
  return [from, ...inside, to];
}

/** Half the width of a castle wall's outer face at `s`, just inside the rail. */
export function castleHalfWidth(template: FormedTemplate, s: number): number {
  return CASTLE_INSET * hullPoint(template, s, 1)[2];
}

export function hullStylesOf(palette: HullPalette, edgeId: number, seed: number): HullStyles {
  const wood = (color: LinearRGB): VertexStyle => ({
    color,
    surface: SURFACE.wood,
    edgeId,
    seed,
    strokeAngle: 0,
    extent: [0, 0],
  });
  return {
    hull: wood(palette.hull),
    wale: wood(palette.wale),
    rail: wood(palette.rail),
    port: wood(palette.port),
    rope: { ...wood(palette.rope), surface: SURFACE.linework },
  };
}
